import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Dashboard,
  QuestionAnswer,
  SmartToy,
  Language,
  Security,
  ManageAccounts,
  Logout,
  Forum,
  Hub,
  Insights,
  ContactPhone,
} from '@mui/icons-material';
import { toggleMobileSidebar } from '../../store/slices/uiSlice';
import { logout } from '../../store/slices/authSlice';

const SIDEBAR_WIDTH = 260;

// roles: 해당 역할만 메뉴 표시. 미지정 시 전체 공개.
const sidebarSections = [
  {
    title: 'MAIN',
    items: [
      { label: '대시보드', icon: <Dashboard />, path: '/nkadmin/dashboard', roles: ['admin'] },
      { label: '연락처 관리', icon: <ContactPhone />, path: '/nkadmin/contacts', roles: ['admin'] },
    ],
  },
  {
    title: 'TELEGRAM',
    items: [
      // 상담관리: 모든 역할(메인관리자/관리자/상담관리)
      { label: '상담 관리', icon: <Forum />, path: '/nkadmin/consultations' },
      // 자동응답 내역: 메인관리자 + 관리자(userdb)
      { label: '자동응답 내역', icon: <SmartToy />, path: '/nkadmin/auto-replies', roles: ['admin', 'userdb'] },
      // 봇 관리: 메인관리자 전용
      { label: '봇 관리', icon: <Hub />, path: '/nkadmin/bots', roles: ['admin'] },
      // 전환 통계: 메인관리자 전용
      { label: '전환 통계', icon: <Insights />, path: '/nkadmin/analytics', roles: ['admin'] },
    ],
  },
  {
    title: '설정',
    items: [
      // Q&A 설정: 메인관리자 + 관리자(userdb)
      { label: 'Q&A 설정', icon: <QuestionAnswer />, path: '/nkadmin/qa', roles: ['admin', 'userdb'] },
      // 봇 설정(공통)·사이트 설정 메뉴는 숨김 (봇 관리로 통합)
    ],
  },
  {
    title: '시스템',
    items: [
      { label: '보안 로그', icon: <Security />, path: '/nkadmin/security-logs', roles: ['admin'] },
      { label: '사용자 관리', icon: <ManageAccounts />, path: '/nkadmin/users', roles: ['admin'] },
    ],
  },
];

const Sidebar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarMobileOpen, featureFlags } = useSelector((state) => state.ui);
  const { userInfo } = useSelector((state) => state.auth);
  const userRole = userInfo?.role;

  // 동적 권한: 연락처 관리는 메인관리자 전용이되, 토글 시 관리자(userdb)에게도 허용
  const effectiveRoles = (item) => {
    if (item.path === '/nkadmin/contacts') {
      return featureFlags?.contactsAccessUserdb ? ['admin', 'userdb'] : ['admin'];
    }
    if (item.path === '/nkadmin/analytics') {
      return featureFlags?.analyticsAccessUserdb ? ['admin', 'userdb'] : ['admin'];
    }
    return item.roles;
  };

  // role 기반 메뉴 필터링 (roles 미지정=전체 공개, 지정 시 해당 역할만 표시)
  const filteredSections = sidebarSections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        const roles = effectiveRoles(item);
        return !roles || roles.includes(userRole);
      }),
    }))
    .filter((section) => section.items.length > 0);

  const handleNav = (path) => {
    navigate(path);
    if (isMobile) dispatch(toggleMobileSidebar());
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/nklogin');
  };

  const drawerContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#0d1321',
        color: '#8892a4',
      }}
    >
      {/* 로고 */}
      <Box sx={{ px: 2.5, py: 2.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h6" sx={{ fontFamily: "'Noto Serif KR', serif", color: '#fff', fontWeight: 500, letterSpacing: 4 }}>
          통합관리
        </Typography>
        <Typography variant="caption" sx={{ color: '#4a5568', ml: 'auto' }}>
          Admin
        </Typography>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

      {/* 메뉴 */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        {filteredSections.map((section) => (
          <Box key={section.title} sx={{ mb: 0.5 }}>
            <Typography
              variant="overline"
              sx={{
                px: 2.5,
                py: 1,
                display: 'block',
                color: '#4a5568',
                fontSize: '0.65rem',
                fontWeight: 700,
                letterSpacing: 1.5,
              }}
            >
              {section.title}
            </Typography>
            <List disablePadding>
              {section.items.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <ListItem key={item.path} disablePadding sx={{ position: 'relative' }}>
                    {isActive && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 0,
                          top: 4,
                          bottom: 4,
                          width: 3,
                          bgcolor: '#1976d2',
                          borderRadius: '0 3px 3px 0',
                        }}
                      />
                    )}
                    <ListItemButton
                      onClick={() => handleNav(item.path)}
                      sx={{
                        mx: 1,
                        borderRadius: 1,
                        py: 0.8,
                        bgcolor: isActive ? 'rgba(25,118,210,0.15)' : 'transparent',
                        color: isActive ? '#fff' : '#8892a4',
                        '&:hover': {
                          bgcolor: isActive
                            ? 'rgba(25,118,210,0.2)'
                            : 'rgba(255,255,255,0.06)',
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 36,
                          color: isActive ? '#1976d2' : '#8892a4',
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: isActive ? 600 : 400 }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      {/* 로그아웃 */}
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
      <List disablePadding sx={{ p: 1 }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 1,
            color: '#8892a4',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: '#8892a4' }}>
            <Logout />
          </ListItemIcon>
          <ListItemText primary="로그아웃" primaryTypographyProps={{ fontSize: '0.875rem' }} />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <Box component="nav" sx={{ width: { md: SIDEBAR_WIDTH }, flexShrink: { md: 0 } }}>
      {/* 모바일: 임시 Drawer */}
      <Drawer
        variant="temporary"
        open={sidebarMobileOpen}
        onClose={() => dispatch(toggleMobileSidebar())}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH, border: 'none' },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* 데스크톱: 영구 Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH, border: 'none' },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
